import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiReady, formatApiError, humanPause, newId } from "../../lib/api";
import { enquiryTypes } from "../../content/site";
import { services } from "../../content/services";

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
const inputCls = "mt-1 h-10 w-full rounded-xl border border-white/10 bg-black/40 px-3 text-sm text-stone-100 outline-none placeholder:text-stone-500 focus:border-orange-500 aria-[invalid=true]:border-red-400";
const typeNoun = { quote: "quote request", site_survey: "site survey request", consultation: "consultation request", contact: "enquiry" };

const F = ({ label, error, children, optional }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-400">{label}{optional && <span className="normal-case tracking-normal text-stone-500"> · optional</span>}</span>
    {children}
    {error && <span role="alert" className="mt-1 block text-xs text-red-300">{error}</span>}
  </label>
);

export const EnquiryResult = ({ reference, token, ack, email }) => {
  const trackPath = `/track?ref=${encodeURIComponent(reference)}#t=${encodeURIComponent(token ?? "")}`;
  return (
    <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.07] p-4 text-sm" data-testid="concierge-enquiry-result">
      <p className="flex items-center gap-2 font-semibold text-emerald-200"><Check className="h-4 w-4" /> Request received by TechCare</p>
      <p className="mt-2 text-stone-300">Your reference number:</p>
      <div className="mt-1 flex items-center gap-2">
        <span className="rounded-lg bg-black/60 px-3 py-1.5 font-mono text-base tracking-wider text-orange-300" data-testid="concierge-result-reference">{reference}</span>
        <button type="button" aria-label="Copy reference" onClick={() => { navigator.clipboard?.writeText(reference); toast.success("Reference copied"); }}
          className="grid h-8 w-8 place-items-center rounded-lg border border-white/15 text-stone-300 hover:border-orange-500" data-testid="concierge-copy-reference"><Copy className="h-3.5 w-3.5" /></button>
      </div>
      {ack === "queued" && <p className="mt-2 text-xs text-stone-400">A confirmation email with your private tracking link is on its way to {email}.</p>}
      {token && (
        <Link to={trackPath} data-testid="concierge-result-track-link"
          className="mt-3 inline-flex h-9 items-center rounded-full bg-orange-500 px-4 text-xs font-semibold text-black hover:bg-orange-400">Track my request</Link>
      )}
      <p className="mt-3 text-xs text-stone-400">Keep your tracking link private. Never share it in chat.</p>
    </div>
  );
};

export const EnquiryReview = ({ draft, startedAt, onDone, onCancel }) => {
  const [f, setF] = useState({ type: draft.type, services: draft.services ?? [], location: draft.location ?? "", message: draft.message ?? "",
    name: "", email: "", phone: "", organisation: "", consent: false, website: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");
  const started = useRef(startedAt ?? Date.now());
  const submissionId = useRef(newId());
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const toggle = (n) => setF((x) => ({ ...x, services: x.services.includes(n) ? x.services.filter((s) => s !== n) : [...x.services, n] }));

  const confirm = async () => {
    const e = {};
    if (f.name.trim().length < 2) e.name = "Please enter your name.";
    if (!emailOk(f.email.trim())) e.email = "Please enter a valid email.";
    if (f.phone && !/^[0-9+()\-\s]{6,40}$/.test(f.phone)) e.phone = "Digits, spaces, + ( ) or - only.";
    if (f.message.trim().length < 10) e.message = "Please add a little more detail (10+ characters).";
    if (f.type === "site_survey" && !f.location.trim()) e.location = "Site location is needed for a survey.";
    if (!f.consent) e.consent = "Please confirm we may contact you.";
    setErrors(e);
    if (Object.keys(e).length) return;
    if (!apiReady) { setServerError("Enquiries are not connected on this deployment yet."); return; }
    setBusy(true); setServerError("");
    try {
      await humanPause(started.current);
      const { data } = await api.post("/enquiries", {
        type: f.type, name: f.name, email: f.email, phone: f.phone || null, organisation: f.organisation || null,
        location: f.location || null, services: f.services, message: f.message, consent: f.consent, website: f.website,
        elapsed_ms: Date.now() - started.current, source_page: "/concierge", submission_id: submissionId.current,
      });
      onDone({ reference: data?.reference ?? "", token: data?.tracking_token ?? null, ack: data?.acknowledgement, email: f.email.trim() });
    } catch (err) {
      setServerError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-orange-500/30 bg-[#17120F] p-4" data-testid="concierge-review-card">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-orange-300">Review before sending</p>
      <p className="mt-1 text-sm text-stone-300">Nothing is sent until you press Confirm. Contact details go straight to our secure enquiry system, not to the AI. They are used only to respond to your request. Privacy questions: hello@techcare.live.</p>
      <div className="mt-3 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Request type">
        {enquiryTypes.map((t) => (
          <button key={t.id} type="button" role="radio" aria-checked={f.type === t.id} onClick={() => setF((x) => ({ ...x, type: t.id }))} data-testid={`concierge-review-type-${t.id}`}
            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${f.type === t.id ? "border-orange-500 bg-orange-500 text-black" : "border-white/15 text-stone-300 hover:border-orange-500"}`}>{t.label}</button>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5" aria-label="Services">
        {services.map((s) => (
          <button key={s.slug} type="button" aria-pressed={f.services.includes(s.name)} onClick={() => toggle(s.name)} data-testid={`concierge-review-service-${s.slug}`}
            className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${f.services.includes(s.name) ? "border-orange-400/70 bg-orange-500/20 text-orange-100" : "border-white/10 text-stone-400 hover:border-white/30"}`}>{s.name}</button>
        ))}
      </div>
      <div className="mt-3 space-y-3">
        {(f.type === "site_survey" || f.location) && (
          <F label="Site location" error={errors.location}><input className={inputCls} value={f.location} onChange={set("location")} maxLength={160} placeholder="Town / area" aria-invalid={!!errors.location} data-testid="concierge-review-location" /></F>
        )}
        <F label="Your request" error={errors.message}>
          <textarea rows={3} className={`${inputCls} h-auto py-2`} value={f.message} onChange={set("message")} maxLength={4000} aria-invalid={!!errors.message} data-testid="concierge-review-message" />
        </F>
        <div className="grid gap-3 sm:grid-cols-2">
          <F label="Full name" error={errors.name}><input className={inputCls} value={f.name} onChange={set("name")} autoComplete="name" maxLength={120} aria-invalid={!!errors.name} data-testid="concierge-review-name" /></F>
          <F label="Email" error={errors.email}><input type="email" className={inputCls} value={f.email} onChange={set("email")} autoComplete="email" maxLength={200} aria-invalid={!!errors.email} data-testid="concierge-review-email" /></F>
          <F label="Phone" optional error={errors.phone}><input type="tel" className={inputCls} value={f.phone} onChange={set("phone")} autoComplete="tel" maxLength={40} placeholder="+254 …" aria-invalid={!!errors.phone} data-testid="concierge-review-phone" /></F>
          <F label="Organisation" optional><input className={inputCls} value={f.organisation} onChange={set("organisation")} maxLength={160} data-testid="concierge-review-org" /></F>
        </div>
        <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden"><input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} /></div>
        <label className="flex items-start gap-2 text-xs text-stone-300">
          <input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 shrink-0 accent-orange-500" data-testid="concierge-review-consent" />
          <span>I agree that TechCare Technologies may store these details and contact me about this {typeNoun[f.type] ?? "enquiry"}.</span>
        </label>
        {errors.consent && <p role="alert" className="text-xs text-red-300">{errors.consent}</p>}
        {serverError && <p role="alert" className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200" data-testid="concierge-review-error">{serverError}</p>}
      </div>
      <div className="mt-4 flex gap-2">
        <button type="button" onClick={confirm} disabled={busy} data-testid="concierge-review-confirm"
          className="tc-btn-primary inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-full text-sm font-semibold disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} {busy ? "Sending…" : "Confirm and send"}
        </button>
        <button type="button" onClick={onCancel} disabled={busy} data-testid="concierge-review-cancel"
          className="h-10 rounded-full border border-white/15 px-4 text-sm text-stone-300 hover:border-white/40">Cancel</button>
      </div>
    </div>
  );
};
